import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { GoogleLogo, Sparkle, Gift, Timer, ArrowRight } from "@phosphor-icons/react";
import { Testimonials } from "@/components/Testimonials";
import { PricingSection } from "@/components/Pricing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Field } from "@/components/common";

export const Brand = ({ className = "", light = true, variant = "full" }) => {
  const src = { full: light ? "/logo-light.png" : "/logo.png", mark: light ? "/logo-r-light.png" : "/logo-r.png", copyright: light ? "/logo-copyright-light.png" : "/logo-copyright.png" }[variant];
  return (
    <div className={`inline-flex items-center ${className}`} data-testid="brand">
      <img src={src} alt="BİZİMCARİ" className="h-[2.2em] w-auto" />
    </div>
  );
};

export const Copyright = ({ light = true, className = "" }) => (
  <img src={light ? "/logo-copyright-light.png" : "/logo-copyright.png"} alt="© BİZİMCARİ 2026" className={`h-10 w-auto opacity-80 ${className}`} data-testid="brand-copyright" />
);

const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const PROMO_MAIN = "14 Gün Ücretsiz Dene – Kredi kartı gerekmez, tüm Pro özellikleri açık";
const PROMO_SHORT = "İlk 14 gün bizden! Hemen ücretsiz başla";

const PromoRibbon = () => {
  const items = Array.from({ length: 6 }, (_, i) => (i % 2 ? PROMO_SHORT : PROMO_MAIN));
  return (
    <div className="fixed inset-x-0 top-0 z-40 overflow-hidden bg-zinc-950 py-2 text-[12px] font-semibold tracking-wide text-white" data-testid="promo-ribbon">
      <div className="promo-marquee gap-10">
        {[...items, ...items].map((t, i) => (
          <span key={i} className="flex items-center gap-3 whitespace-nowrap">
            <Sparkle weight="fill" className="text-[#fdba74]" />
            <span className={i % 2 ? "text-[#2dd4bf]" : ""}>{t}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const PromoBadge = () => (
  <Link to="/kayit" className="promo-float relative inline-flex items-center gap-3 rounded-full border border-[#fdba74]/40 bg-[#fdba74]/10 px-4 py-2 backdrop-blur transition-colors hover:bg-[#fdba74]/20" data-testid="promo-badge">
    <span className="promo-glow flex h-7 w-7 items-center justify-center rounded-full bg-[#fdba74] text-zinc-950"><Gift weight="fill" size={16} /></span>
    <span className="text-sm font-semibold text-[#fdba74]">{PROMO_SHORT}</span>
    <ArrowRight weight="bold" className="text-[#fdba74]" />
  </Link>
);

const PromoCard = () => (
  <Link to="/kayit" className="promo-shimmer fade-up block rounded-[6px] border border-teal-200 bg-gradient-to-r from-teal-50 via-white to-orange-50 px-4 py-3 transition-transform hover:-translate-y-0.5" data-testid="promo-card">
    <div className="flex items-start gap-3">
      <span className="promo-glow mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#fdba74] text-zinc-950"><Timer weight="fill" size={18} /></span>
      <div className="flex-1">
        <p className="font-display text-base font-extrabold leading-tight text-zinc-900">14 Gün <span className="text-teal-700">Ücretsiz</span> Dene</p>
        <p className="mt-0.5 text-xs text-zinc-600">Kredi kartı gerekmez, tüm Pro özellikleri açık.</p>
      </div>
      <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-zinc-950 px-2.5 py-1 text-[11px] font-bold text-white" data-testid="promo-card-cta">Başla <ArrowRight weight="bold" /></span>
    </div>
  </Link>
);

function Showcase() {
  const tiles = [["Cari Ekstre", "bg-[#2dd4bf]"], ["Stok Takibi", "bg-[#fdba74]"], ["Online Tahsilat", "bg-[#7dd3fc]"]];
  return (
    <div className="relative hidden flex-col justify-between overflow-hidden bg-zinc-950 p-12 pt-20 text-white grain lg:flex" data-testid="login-showcase">
      <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#2dd4bf]/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 left-10 h-80 w-80 rounded-full bg-[#7dd3fc]/15 blur-3xl" />
      <Brand className="relative text-2xl" />
      <div className="relative max-w-lg">
        <PromoBadge />
        <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#7dd3fc]">Ön Muhasebe · Stok · Sipariş</p>
        <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight xl:text-5xl">
          Cari hesaplar, stok ve kasa<br /><span className="text-[#2dd4bf]">tek bir defterde.</span>
        </h1>
        <p className="mt-6 text-zinc-400">Satış, alış ve siparişlerinizi kaydedin; bakiyeleri ve günlük hareketleri anında görün, her şeyi raporlayın.</p>
        <p className="mt-4 text-sm font-semibold text-[#2dd4bf]" data-testid="promo-showcase-text">{PROMO_MAIN}</p>
      </div>
      <div className="relative space-y-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-zinc-500">Kullanıcılarımız ne diyor?</p>
        <Testimonials />
        <div className="grid grid-cols-3 gap-3 font-num text-xs">
          {tiles.map(([t, c]) => (
            <div key={t} className="flex items-center gap-2 border border-white/10 bg-white/5 px-4 py-3 text-zinc-300">
              <span className={`h-2 w-2 rounded-full ${c}`} />{t}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(loc.state?.error || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email, password);
      toast.success("Hoş geldiniz");
      nav(loc.state?.from || "/", { replace: true });
    } catch (e2) {
      setErr(errMsg(e2));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen">
      <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <PromoRibbon />
      <Showcase />
      <div className="flex items-center justify-center bg-gradient-to-br from-white via-white to-[#e0f2fe] p-6 pt-16">
        <form onSubmit={submit} className="fade-up w-full max-w-sm space-y-6" data-testid="login-form">
          <PromoCard />
          <div>
            <Brand className="text-3xl lg:hidden" light={false} />
            <div className="mt-4 h-1 w-14 bg-[#fdba74]" />
            <h2 className="mt-3 font-display text-2xl font-bold">Giriş Yap</h2>
            <p className="mt-1 text-sm text-muted-foreground">Bizim Cari hesabınızla oturum açın.</p>
          </div>
          <Field label="E-posta">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="rounded-[4px] focus-visible:ring-[#2dd4bf]" />
          </Field>
          <Field label="Şifre">
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="rounded-[4px] focus-visible:ring-[#2dd4bf]" />
          </Field>
          {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{err}</div>}
          <Button type="submit" disabled={busy} className="h-10 w-full rounded-[4px] bg-teal-600 text-white transition-colors hover:bg-teal-700" data-testid="login-submit-button">
            {busy ? "Giriş yapılıyor…" : "Giriş Yap"}
          </Button>
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-muted-foreground"><span className="h-px flex-1 bg-border" />veya<span className="h-px flex-1 bg-border" /></div>
          <Button type="button" variant="outline" onClick={googleLogin} className="h-10 w-full rounded-[4px] border-zinc-900 transition-colors hover:bg-[#fff7ed]" data-testid="login-google-button">
            <GoogleLogo weight="bold" /> Google ile Giriş Yap
          </Button>
          <p className="text-center text-[11px] text-muted-foreground">Google ile giriş yalnızca sisteme kayıtlı e-posta adresleri için çalışır.</p>
          <p className="text-center text-sm text-muted-foreground">
            Hesabınız yok mu? <Link to="/kayit" className="font-semibold text-teal-700 hover:underline" data-testid="login-register-link">14 gün ücretsiz deneyin</Link>
          </p>
          <div className="lg:hidden"><Testimonials dark={false} /></div>
          <a href="#planlar" className="block text-center text-xs font-semibold text-zinc-500 hover:text-zinc-800" data-testid="login-plans-anchor">Planları ve fiyatları gör ↓</a>
        </form>
      </div>
      </div>
      <div id="planlar" className="bg-zinc-950 px-6 py-16 text-white grain lg:px-12">
        <div className="mx-auto max-w-6xl"><PricingSection dark /></div>
        <div className="mx-auto mt-12 flex max-w-6xl flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6 text-xs text-zinc-500">
          <Copyright />
          <span>Tüm hakları saklıdır · WhatsApp 0533 070 74 31</span>
        </div>
      </div>
    </div>
  );
}
